import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { getInitials } from '../../lib/utils';
import { Send, ShieldAlert, BadgeCheck, Loader2, Paperclip, FileUp, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { AssessmentFormBubble, AttachmentBubble, DietPlanBubble, PaymentRequestBubble, VideoCallBubble } from './ConsultBubbles';
import { DietAssessmentForm } from './DietAssessmentForm';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { VaultPickerDialog } from './VaultPickerDialog';

const OPEN = ['waiting', 'active'];

/**
 * Shared consultation thread (nutritionist + learner + admin read-only).
 * Polls every 2s. Renders identity-swapped messages, a contact-blocked warning, the provider
 * credential card, V66 rich bubbles (shared documents, payment requests, video calls, episode
 * markers) and (optionally) a composer with saved-reply chips + the learner's Share-document button.
 */
export const ConsultThread = ({
  consultId,
  fetchUrl,                 // override for admin read-only
  composer = true,
  savedReplies = [],
  onMeta,                   // callback(consultMeta)
}) => {
  const [consult, setConsult] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [card, setCard] = useState(null);
  const [picker, setPicker] = useState(false);
  const [planBusy, setPlanBusy] = useState(false);
  const planInputRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const url = fetchUrl || `/api/medical/consult/${consultId}`;

  const load = useCallback(async () => {
    try {
      const res = await api.get(url);
      setConsult(res.data);
      onMeta && onMeta(res.data);
    } catch (e) { /* silent poll */ }
  }, [url]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let stop = false;
    const run = () => api.get(url).then(res => { if (!stop) { setConsult(res.data); onMeta && onMeta(res.data); } }).catch(() => {});
    run();
    const t = setInterval(run, 2000);
    return () => { stop = true; clearInterval(t); };
  }, [url]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [consult?.messages?.length]);

  const send = async () => {
    const content = text.trim();
    if (!content) return;
    setSending(true);
    try {
      const res = await api.post(`/api/medical/consult/${consultId}/message`, { content });
      setText('');
      if (res.data?.pii_blocked) {
        toast.warning("For everyone's safety, contact details (phone/email) can't be shared here.");
      }
      await load();
      inputRef.current?.focus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send');
    } finally {
      setSending(false);
    }
  };

  // V73 — the dietician's tools exist only when a diet plan is on her account.
  const [dietState, setDietState] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formBusy, setFormBusy] = useState(false);
  const dietEntitled = !!dietState?.diet_plan?.entitled;

  const loadDietState = useCallback(async () => {
    try {
      const res = await api.get(`/api/medical/consult/${consultId}/form`);
      setDietState(res.data);
    } catch (e) { /* non-blocking */ }
  }, [consultId]);

  useEffect(() => {
    loadDietState();
    // the admin may grant the diet plan while this chat is open — follow the poll so the
    // dietician's tools light up without her reloading the portal
    const t = setInterval(loadDietState, 10000);
    return () => clearInterval(t);
  }, [loadDietState, consult?.messages?.length]);

  const sendForm = async (formType) => {
    setFormBusy(true);
    try {
      await api.post(`/api/medical/consult/${consultId}/form`, { form_type: formType, note: text.trim() });
      setText('');
      toast.success(`${formType === 'international' ? 'International' : 'India'} form sent`);
      await load(); await loadDietState();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === 'object' ? d?.message : d) || 'Could not send the form');
    } finally { setFormBusy(false); }
  };

  const reopenForm = async () => {
    try {
      await api.post(`/api/medical/consult/${consultId}/form/reopen`);
      toast.success('Reopened — she can edit her answers now');
      await load(); await loadDietState();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === 'object' ? d?.message : d) || 'Could not reopen the form');
    }
  };

  const openCard = async () => {
    try {
      const res = await api.get(`/api/medical/consult/${consultId}/provider-card`);
      setCard(res.data);
    } catch (e) { /* ignore */ }
  };

  // V72 — the dietician shares the diet plan itself. Stored in object storage and filed
  // in the learner's Vault; the thread gets a tappable card.
  const sharePlan = async (file) => {
    setPlanBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('note', text.trim() || 'Here\'s your diet plan.');
      await api.post(`/api/medical/consult/${consultId}/plan`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setText('');
      toast.success('Diet plan shared');
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === 'object' ? d?.message : d) || 'Could not share the plan');
    } finally { setPlanBusy(false); }
  };

  if (!consult) {
    return <div className="flex items-center justify-center h-full text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }

  const messages = consult.messages || [];
  const closed = !OPEN.includes(consult.status);
  const role = consult.viewer_role;
  const isLearner = role === 'learner';

  const renderBody = (m, mine) => {
    if (m.kind === 'assessment_form') {
      return <AssessmentFormBubble consultId={consultId} message={m} mine={mine} role={role}
        onFill={() => setFormOpen(true)} onReopen={reopenForm} />;
    }
    if (m.attachment?.kind === 'diet_assessment') return <DietPlanBubble message={m} mine={mine} />;
    if (m.attachment?.kind === 'diet_plan') return <DietPlanBubble message={m} mine={mine} />;
    if (m.kind === 'attachment') return <AttachmentBubble consultId={consultId} message={m} mine={mine} />;
    if (m.kind === 'payment_request') return <PaymentRequestBubble message={m} viewerRole={role} closed={closed} onChanged={load} />;
    if (m.kind === 'video_call') return <VideoCallBubble message={m} viewerRole={role} onChanged={load} />;
    return <p className="whitespace-pre-wrap leading-relaxed">{m.content}</p>;
  };

  return (
    <div className="flex flex-col h-full" data-testid="consult-thread">
      {/* messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-muted/20">
        {messages.map((m, i) => {
          if (m.sender_type === 'system') {
            const episode = m.kind === 'episode';
            return (
              <div key={m.message_id || i} className="text-center">
                <span className={`text-[11px] px-3 py-1 rounded-full ${episode ? 'bg-emerald-100 text-emerald-800 font-medium' : 'text-muted-foreground bg-muted'}`}
                  data-testid={episode ? 'episode-marker' : 'system-message'}>{m.content}</span>
              </div>
            );
          }
          const isProvider = m.sender_type === 'provider';
          const mine = (role === 'provider' && isProvider) || (role === 'learner' && !isProvider);
          const rich = ['payment_request', 'video_call'].includes(m.kind);
          return (
            <div key={m.message_id || i} className={`flex gap-2 ${mine ? 'justify-end' : 'justify-start'}`}>
              {!mine && <Avatar className="h-7 w-7 shrink-0"><AvatarFallback className="bg-emerald-100 text-emerald-700 text-[10px]">{getInitials(m.sender_name)}</AvatarFallback></Avatar>}
              <div className={`max-w-[80%] text-sm ${rich ? '' : `rounded-2xl px-3 py-2 ${mine ? 'bg-emerald-600 text-white' : 'bg-white border'}`}`}>
                {!mine && isProvider && !rich && (
                  <button onClick={openCard} className="text-[11px] font-semibold text-emerald-700 mb-0.5 flex items-center gap-1" data-testid="provider-name-btn">
                    {m.sender_name} <BadgeCheck className="h-3 w-3" />
                  </button>
                )}
                {renderBody(m, mine)}
                {m.had_pii_blocked && (
                  <span className={`mt-1 flex items-center gap-1 text-[10px] ${mine ? 'text-emerald-100' : 'text-amber-600'}`} data-testid="pii-warning-chip">
                    <ShieldAlert className="h-3 w-3" /> Contact details hidden for safety
                  </span>
                )}
              </div>
            </div>
          );
        })}
        {messages.filter(m => m.sender_type !== 'system').length === 0 && (
          <p className="text-center text-sm text-muted-foreground mt-8">No messages yet.</p>
        )}
      </div>

      {/* provider credential card */}
      {card && (
        <div className="border-t bg-emerald-50/60 px-4 py-3 text-xs" data-testid="provider-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-emerald-900 flex items-center gap-1">{card.display_name} {card.is_verified && <BadgeCheck className="h-3.5 w-3.5 text-emerald-600" />}</p>
              <p className="text-emerald-800">{card.qualification} · {card.experience_years} yrs · {(card.languages || []).join(', ')}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setCard(null)}>Close</Button>
          </div>
        </div>
      )}

      {/* composer */}
      {composer && !closed && (
        <div className="border-t bg-white p-3">
          {savedReplies.length > 0 && (
            <div className="flex gap-1.5 overflow-x-auto pb-2 mb-1" data-testid="saved-reply-chips">
              {savedReplies.map(sr => (
                <button key={sr.id} onClick={() => { setText(sr.body); inputRef.current?.focus(); api.post(`/api/medical/saved-replies/${sr.id}/used`).catch(() => {}); }}
                  className="shrink-0 text-xs px-2.5 py-1 rounded-full border bg-muted/50 hover:bg-emerald-50 hover:border-emerald-300 transition-colors" data-testid="saved-reply-chip">
                  {sr.title}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            {isLearner && (
              <Button type="button" variant="outline" size="icon" className="shrink-0 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                onClick={() => setPicker(true)} title="Share a document from your Medical Vault" data-testid="consult-attach-btn">
                <Paperclip className="h-4 w-4" />
              </Button>
            )}
            {role === 'provider' && (
              <>
                <input ref={planInputRef} type="file" accept="application/pdf,image/*" className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) sharePlan(f); e.target.value = ''; }}
                  data-testid="consult-diet-plan-input" />
                <Button type="button" variant="outline" size="icon" disabled={planBusy || !dietEntitled}
                  className="shrink-0 border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
                  onClick={() => planInputRef.current?.click()}
                  title={dietEntitled ? 'Share the diet plan (PDF)' : 'No diet plan on her account yet — an admin can add it'}
                  data-testid="consult-share-plan-btn">
                  {planBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button type="button" variant="outline" size="icon" disabled={formBusy || !dietEntitled}
                      className="shrink-0 border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
                      title={dietEntitled ? 'Send the assessment form' : 'No diet plan on her account yet — an admin can add it'}
                      data-testid="consult-send-form-btn">
                      {formBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardList className="h-4 w-4" />}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-60">
                    <DropdownMenuItem onClick={() => sendForm('india')} data-testid="consult-send-form-india">
                      India form · 15 questions
                      {dietState?.suggested_form_type === 'india' && <span className="ml-auto text-[10px] text-emerald-700">suggested</span>}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => sendForm('international')} data-testid="consult-send-form-international">
                      International · 20 questions
                      {dietState?.suggested_form_type === 'international' && <span className="ml-auto text-[10px] text-emerald-700">suggested</span>}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
            <Input ref={inputRef} value={text} onChange={e => setText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Type a message…" data-testid="consult-message-input" />
            <Button onClick={send} disabled={sending || !text.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="consult-send-btn">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}
      {closed && composer && (
        <div className="border-t bg-muted/40 p-3 text-center text-sm text-muted-foreground" data-testid="consult-closed-banner">
          This conversation is closed.
        </div>
      )}
      {isLearner && <VaultPickerDialog open={picker} onOpenChange={setPicker} consultId={consultId} onShared={load} />}
      {isLearner && <DietAssessmentForm consultId={consultId} open={formOpen} onClose={() => setFormOpen(false)}
        onSubmitted={() => { load(); loadDietState(); }} />}
    </div>
  );
};
